import html2canvas from "html2canvas";

interface Options {
  scale?: number;
  backgroundColor?: any;
  maxDimension?: number; // maximum width/height to avoid OOM (pixels)
}

/**
 * Safe html2canvas wrapper.
 * - Limits scale so the produced canvas won't exceed maxDimension.
 * - Retries with smaller scale if the first attempt fails.
 * - Returns dataURL string on success, or null on failure.
 */
export default async function safeHtml2canvas(
  el: HTMLElement,
  opts: Options = {}
): Promise<string | null> {
  const { scale = 2, backgroundColor = null, maxDimension = 4096 } = opts;

  try {
    if (!el) return null;

    const w = el.clientWidth || el.offsetWidth || el.scrollWidth || 0;
    const h = el.clientHeight || el.offsetHeight || el.scrollHeight || 0;
    const maxSide = Math.max(w, h, 1);

    // compute a safe scale so that maxSide * scale <= maxDimension
    const safeScale = Math.max(
      1,
      Math.floor(Math.min(scale, Math.max(1, Math.floor(maxDimension / maxSide))))
    );

    // Try with the computed safe scale; if it fails, retry with scale=1
    try {
      const canvas = await html2canvas(el, {
        scale: safeScale,
        backgroundColor,
      } as any);
      return canvas.toDataURL("image/png");
    } catch (err) {
      // Try a last-ditch attempt with scale=1
      try {
        const canvas = await html2canvas(el, {
          scale: 1,
          backgroundColor,
        } as any);
        return canvas.toDataURL("image/png");
      } catch (err2) {
        console.warn("safeHtml2canvas: failed both attempts", err, err2);
        return null;
      }
    }
  } catch (err) {
    console.warn("safeHtml2canvas unexpected error", err);
    return null;
  }
}
